import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Chip,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Card,
  CardActionArea,
  Divider,
  Tooltip,
  CircularProgress,
  Alert as MuiAlert,
  Snackbar,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add,
  Delete,
  Send,
  Save,
  Email,
  Schedule as ScheduleIcon,
  FilterAlt,
  Refresh,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import {
  DigestConfig,
  DigestSection,
  DigestSchedule,
  DASHBOARD_SECTIONS,
  sectionTitle,
  fleetCounts,
  scheduleLabel,
  WEEKDAYS,
} from '../utils/digestFilters';

const API =
  process.env.REACT_APP_API_ENDPOINT || 'https://fg0jf2txt9.execute-api.us-east-2.amazonaws.com/prod';

const HEALTH_COLOR: Record<string, string> = { red: '#d32f2f', yellow: '#ed6c02', green: '#2e7d32' };
const HEALTH_LABEL: Record<string, string> = { red: 'Critical', yellow: 'Warning', green: 'Healthy' };

const blankConfig = (): DigestConfig => ({
  name: 'New digest',
  enabled: true,
  recipients: [],
  schedules: [{ freq: 'weekly', weekday: 0, hour: 13 }],
  sections: [{ id: 'meter-fleet-status' }, { id: 'meter-control-charts', filters: {}, include_regression: true }],
});

const fmt = (v: any) => (typeof v === 'number' ? v.toFixed(4) : v != null ? Number(v).toFixed(4) : '—');

const DigestBuilder: React.FC = () => {
  const theme = useTheme();
  const { getIdTokenClaims } = useAuth0();

  const [configs, setConfigs] = useState<DigestConfig[]>([]);
  const [meters, setMeters] = useState<any[]>([]);
  const [draft, setDraft] = useState<DigestConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recipInput, setRecipInput] = useState('');
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const auth = useCallback(async () => {
    const token = (await getIdTokenClaims())?.__raw;
    return { Authorization: `Bearer ${token}` };
  }, [getIdTokenClaims]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await auth();
      const [dRes, mRes] = await Promise.all([
        fetch(`${API}/digests`, { headers }),
        fetch(`${API}/meters/dashboard`, { headers }),
      ]);
      const dJson = await dRes.json();
      const mJson = await mRes.json();
      setConfigs(dJson.digests || []);
      setMeters(mJson.meter_products || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load digests.');
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    load();
  }, [load]);

  const distinctProducts = useMemo(
    () => Array.from(new Set(meters.map((m) => m.product_type).filter(Boolean))).sort(),
    [meters],
  );

  // ---- persistence ----
  const save = async (): Promise<DigestConfig | null> => {
    if (!draft) return null;
    setSaving(true);
    setError(null);
    try {
      const headers = { ...(await auth()), 'Content-Type': 'application/json' };
      const isNew = !draft.config_id;
      const resp = await fetch(isNew ? `${API}/digests` : `${API}/digests/${encodeURIComponent(draft.config_id!)}`, {
        method: isNew ? 'POST' : 'PUT',
        headers,
        body: JSON.stringify(draft),
      });
      if (!resp.ok) throw new Error(`Save failed (${resp.status})`);
      const saved = (await resp.json()).digest as DigestConfig;
      setDraft(saved);
      setConfigs((prev) => {
        const others = prev.filter((c) => c.config_id !== saved.config_id);
        return [...others, saved].sort((a, b) => a.name.localeCompare(b.name));
      });
      setToast('Digest saved.');
      return saved;
    } catch (e: any) {
      setError(e?.message || 'Save failed.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const remove = async (cfg: DigestConfig) => {
    if (!cfg.config_id) {
      setDraft(null);
      return;
    }
    try {
      const headers = await auth();
      await fetch(`${API}/digests/${encodeURIComponent(cfg.config_id)}`, { method: 'DELETE', headers });
      setConfigs((prev) => prev.filter((c) => c.config_id !== cfg.config_id));
      if (draft?.config_id === cfg.config_id) setDraft(null);
      setToast('Digest deleted.');
    } catch (e: any) {
      setError(e?.message || 'Delete failed.');
    }
  };

  const testSend = async () => {
    if (!draft) return;
    setTesting(true);
    setError(null);
    try {
      // Ensure it's saved (test-send loads the stored config server-side).
      const saved = draft.config_id ? draft : await save();
      if (!saved?.config_id) throw new Error('Save the digest first.');
      const headers = await auth();
      const resp = await fetch(`${API}/digests/${encodeURIComponent(saved.config_id)}/test`, {
        method: 'POST',
        headers,
      });
      const body = await resp.json();
      if (resp.ok && body?.result?.ok) {
        setToast('Test digest sent to your email.');
      } else {
        const msg = body?.result?.error || 'Test send failed.';
        setError(msg.includes('not verified')
          ? 'Recipient not verified yet — while SES is in the sandbox, recipients must be verified. (Production access removes this.)'
          : msg);
      }
    } catch (e: any) {
      setError(e?.message || 'Test send failed.');
    } finally {
      setTesting(false);
    }
  };

  const refreshPreview = async () => {
    if (!draft) return;
    setPreviewLoading(true);
    setError(null);
    try {
      const headers = { ...(await auth()), 'Content-Type': 'application/json' };
      const resp = await fetch(`${API}/digests/preview`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ config: draft }),
      });
      const body = await resp.json();
      if (resp.ok && body.html) setPreviewHtml(body.html);
      else setError(body.error || 'Preview render failed.');
    } catch (e: any) {
      setError(e?.message || 'Preview render failed.');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Clear the rendered preview when switching between digests.
  useEffect(() => {
    setPreviewHtml(null);
  }, [draft?.config_id]);

  // Live preview: auto-render (debounced) whenever the digest content changes —
  // sections added/removed/reordered, filters, regression, or the name.
  const contentKey = draft ? JSON.stringify({ name: draft.name, sections: draft.sections }) : '';
  useEffect(() => {
    if (!draft) return;
    const timer = setTimeout(() => refreshPreview(), 700);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentKey]);

  // ---- draft mutation helpers ----
  const patch = (p: Partial<DigestConfig>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const patchSection = (i: number, p: Partial<DigestSection>) =>
    setDraft((d) => (d ? { ...d, sections: d.sections.map((s, idx) => (idx === i ? { ...s, ...p } : s)) } : d));
  const removeSection = (i: number) =>
    setDraft((d) => (d ? { ...d, sections: d.sections.filter((_, idx) => idx !== i) } : d));
  const moveSection = (i: number, dir: -1 | 1) =>
    setDraft((d) => {
      if (!d) return d;
      const j = i + dir;
      if (j < 0 || j >= d.sections.length) return d;
      const arr = [...d.sections];
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...d, sections: arr };
    });
  const addSection = (id: string) =>
    setDraft((d) => {
      if (!d) return d;
      const base: DigestSection =
        id === 'meter-control-charts' ? { id, filters: {}, include_regression: true } : { id };
      return { ...d, sections: [...d.sections, base] };
    });
  const patchSchedule = (i: number, p: Partial<DigestSchedule>) =>
    setDraft((d) => (d ? { ...d, schedules: d.schedules.map((s, idx) => (idx === i ? { ...s, ...p } : s)) } : d));

  const addRecipient = () => {
    const e = recipInput.trim();
    if (!e || !draft) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
      setError('Enter a valid email address.');
      return;
    }
    if (!draft.recipients.includes(e)) patch({ recipients: [...draft.recipients, e] });
    setRecipInput('');
  };

  // ---- render ----
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      {/* Left: digest list */}
      <Box sx={{ width: 240, flexShrink: 0 }}>
        <Button fullWidth variant="contained" startIcon={<Add />} onClick={() => setDraft(blankConfig())} sx={{ mb: 1.5 }}>
          New digest
        </Button>
        {configs.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
            No digests yet. Create one to email site reports on a schedule.
          </Typography>
        )}
        {configs.map((c) => (
          <Card
            key={c.config_id}
            variant="outlined"
            sx={{ mb: 1, borderColor: draft?.config_id === c.config_id ? 'primary.main' : undefined }}
          >
            <CardActionArea
              onClick={() => {
                const d = JSON.parse(JSON.stringify(c));
                d.sections = d.sections || [];
                d.schedules = d.schedules || [];
                d.recipients = d.recipients || [];
                setDraft(d);
              }}
              sx={{ p: 1.25 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Email fontSize="small" color={c.enabled ? 'primary' : 'disabled'} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }} noWrap>
                  {c.name}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {(c.schedules || []).map(scheduleLabel).join(' · ') || 'No schedule'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {(c.recipients || []).length} recipient{(c.recipients || []).length === 1 ? '' : 's'}
              </Typography>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      {/* Right: editor + preview */}
      <Box sx={{ flex: 1, minWidth: 360 }}>
        {!draft ? (
          <Box sx={{ textAlign: 'center', py: 10, color: 'text.secondary' }}>
            <Email sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
            <Typography>Select a digest or create a new one.</Typography>
          </Box>
        ) : (
          <>
            {error && (
              <MuiAlert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </MuiAlert>
            )}

            {/* Header actions */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="Digest name"
                value={draft.name}
                onChange={(e) => patch({ name: e.target.value })}
                sx={{ flex: 1, minWidth: 200 }}
              />
              <FormControlLabel
                control={<Switch checked={draft.enabled} onChange={(e) => patch({ enabled: e.target.checked })} />}
                label="Enabled"
              />
              <Button variant="contained" startIcon={<Save />} onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="outlined" startIcon={<Send />} onClick={testSend} disabled={testing}>
                {testing ? 'Sending…' : 'Test send'}
              </Button>
              <Tooltip title="Delete digest">
                <IconButton color="error" onClick={() => remove(draft)}>
                  <Delete />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Recipients */}
            <Section icon={<Email fontSize="small" />} title="Recipients">
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                {draft.recipients.map((r) => (
                  <Chip key={r} label={r} onDelete={() => patch({ recipients: draft.recipients.filter((x) => x !== r) })} />
                ))}
                {draft.recipients.length === 0 && (
                  <Typography variant="caption" color="text.secondary">No recipients yet.</Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="name@company.com"
                  value={recipInput}
                  onChange={(e) => setRecipInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRecipient())}
                  sx={{ flex: 1 }}
                />
                <Button onClick={addRecipient}>Add</Button>
              </Box>
            </Section>

            {/* Schedules */}
            <Section icon={<ScheduleIcon fontSize="small" />} title="Schedule">
              {draft.schedules.map((s, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <InputLabel>Frequency</InputLabel>
                    <Select label="Frequency" value={s.freq} onChange={(e) => patchSchedule(i, { freq: e.target.value as any })}>
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                  </FormControl>
                  {s.freq === 'weekly' && (
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <InputLabel>Day</InputLabel>
                      <Select label="Day" value={s.weekday ?? 0} onChange={(e) => patchSchedule(i, { weekday: Number(e.target.value) })}>
                        {WEEKDAYS.map((d, idx) => <MenuItem key={d} value={idx}>{d}</MenuItem>)}
                      </Select>
                    </FormControl>
                  )}
                  {s.freq === 'monthly' && (
                    <FormControl size="small" sx={{ minWidth: 90 }}>
                      <InputLabel>Day</InputLabel>
                      <Select label="Day" value={s.day_of_month ?? 1} onChange={(e) => patchSchedule(i, { day_of_month: Number(e.target.value) })}>
                        {Array.from({ length: 28 }, (_, k) => k + 1).map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                      </Select>
                    </FormControl>
                  )}
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <InputLabel>Hour (UTC)</InputLabel>
                    <Select label="Hour (UTC)" value={s.hour ?? 13} onChange={(e) => patchSchedule(i, { hour: Number(e.target.value) })}>
                      {Array.from({ length: 24 }, (_, k) => k).map((h) => <MenuItem key={h} value={h}>{String(h).padStart(2, '0')}:00</MenuItem>)}
                    </Select>
                  </FormControl>
                  <IconButton size="small" onClick={() => patch({ schedules: draft.schedules.filter((_, idx) => idx !== i) })}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button size="small" startIcon={<Add />} onClick={() => patch({ schedules: [...draft.schedules, { freq: 'daily', hour: 13 }] })}>
                Add schedule
              </Button>
            </Section>

            {/* Sections */}
            <Section icon={<FilterAlt fontSize="small" />} title="Sections (dashboard items to include, in order)">
              {draft.sections.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  No sections yet — add dashboard items below.
                </Typography>
              )}
              {draft.sections.map((s, i) => {
                const meta = DASHBOARD_SECTIONS.find((d) => d.id === s.id);
                return (
                  <Box key={i} sx={{ p: 1.5, mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <IconButton size="small" sx={{ p: 0 }} disabled={i === 0} onClick={() => moveSection(i, -1)}>
                          <KeyboardArrowUp fontSize="small" />
                        </IconButton>
                        <IconButton size="small" sx={{ p: 0 }} disabled={i === draft.sections.length - 1} onClick={() => moveSection(i, 1)}>
                          <KeyboardArrowDown fontSize="small" />
                        </IconButton>
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
                        {i + 1}. {sectionTitle(s.id)}
                      </Typography>
                      <IconButton size="small" onClick={() => removeSection(i)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                    {meta?.hasFilter && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1, pl: 5, alignItems: 'center' }}>
                        <FilterSelect label="Meters" value={s.filters?.health || 'all'} onChange={(val) => patchSection(i, { filters: { ...s.filters, health: val as any } })}
                          options={[['all', 'All'], ['red', 'Critical'], ['yellow', 'Warning'], ['green', 'Healthy']]} />
                        <FilterSelect label="Product" value={s.filters?.product || 'all'} onChange={(val) => patchSection(i, { filters: { ...s.filters, product: val } })}
                          options={[['all', 'All'], ...distinctProducts.map((p) => [p, p] as [string, string])]} />
                        {meta?.hasRegression && (
                          <FormControlLabel
                            control={<Switch size="small" checked={!!s.include_regression} onChange={(e) => patchSection(i, { include_regression: e.target.checked })} />}
                            label="Regression"
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {DASHBOARD_SECTIONS.filter((d) => !draft.sections.some((s) => s.id === d.id)).map((d) => (
                  <Button key={d.id} size="small" variant="outlined" startIcon={<Add />} onClick={() => addSection(d.id)}>
                    {d.title}
                  </Button>
                ))}
                {DASHBOARD_SECTIONS.every((d) => draft.sections.some((s) => s.id === d.id)) && (
                  <Typography variant="caption" color="text.secondary">All dashboard sections added.</Typography>
                )}
              </Box>
            </Section>

            {/* Live preview */}
            <Section icon={<Email fontSize="small" />} title="Live preview (exactly what recipients receive)">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                {previewLoading ? (
                  <>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">Updating preview…</Typography>
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary">Updates automatically as you edit.</Typography>
                )}
                <Button size="small" startIcon={<Refresh />} onClick={refreshPreview} disabled={previewLoading} sx={{ ml: 'auto' }}>
                  Refresh
                </Button>
              </Box>
              {previewHtml ? (
                <Box
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: '#fff', color: '#222', overflowX: 'auto', opacity: previewLoading ? 0.55 : 1, transition: 'opacity .2s' }}
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              ) : previewLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
              ) : (
                <DigestPreview config={draft} meters={meters} theme={theme} />
              )}
            </Section>
          </>
        )}
      </Box>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );

  // helpers as inner components below
};

const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
      {icon}
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
    </Box>
    <Divider sx={{ mb: 1.5 }} />
    {children}
  </Box>
);

const FilterSelect: React.FC<{ label: string; value: string; options: [string, string][]; onChange: (v: string) => void }> = ({ label, value, options, onChange }) => (
  <FormControl size="small" sx={{ minWidth: 120 }}>
    <InputLabel>{label}</InputLabel>
    <Select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map(([val, lab]) => <MenuItem key={val} value={val}>{lab}</MenuItem>)}
    </Select>
  </FormControl>
);

const DigestPreview: React.FC<{ config: DigestConfig; meters: any[]; theme: any }> = ({ config, meters, theme }) => {
  const counts = fleetCounts(meters);
  const cell = (label: string, value: any, color: string) => (
    <Box sx={{ textAlign: 'center', px: 2, py: 1 }}>
      <Typography sx={{ fontSize: 24, fontWeight: 700, color }}>{value}</Typography>
      <Typography sx={{ fontSize: 10, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</Typography>
    </Box>
  );
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: 'background.paper' }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>{config.name}</Typography>
      <Typography variant="caption" color="text.secondary">CalTerminal · site report</Typography>
      <Box sx={{ display: 'flex', gap: 1, bgcolor: alpha(theme.palette.primary.main, 0.04), borderRadius: 2, my: 1.5, flexWrap: 'wrap' }}>
        {cell('Site health', `${counts.total ? Math.round((counts.healthy / counts.total) * 100) : 0}%`, '#1565c0')}
        {cell('Critical', counts.critical, HEALTH_COLOR.red)}
        {cell('Warning', counts.warning, HEALTH_COLOR.yellow)}
        {cell('Healthy', counts.healthy, HEALTH_COLOR.green)}
        {cell('Drifting', counts.drifting, '#6a1b9a')}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Sections, in order — click "Render exact email" above for the fully rendered report.
      </Typography>
      {(config.sections || []).map((s, i) => {
        const meta = DASHBOARD_SECTIONS.find((d) => d.id === s.id);
        const hlabel = s.filters?.health === 'red' ? 'Critical'
          : s.filters?.health === 'yellow' ? 'Warning'
          : s.filters?.health === 'green' ? 'Healthy' : 'All meters';
        return (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
              {i + 1}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{sectionTitle(s.id)}</Typography>
            {meta?.hasFilter && (
              <Typography variant="caption" color="text.secondary">
                {hlabel}
                {s.filters?.product && s.filters.product !== 'all' ? ` · ${s.filters.product}` : ''}
                {s.include_regression ? ' · +regression' : ''}
              </Typography>
            )}
          </Box>
        );
      })}
      {(config.sections || []).length === 0 && (
        <Typography variant="body2" color="text.secondary">No sections selected.</Typography>
      )}
    </Box>
  );
};

export default DigestBuilder;
